// ═══════════════════════════════════════════════════════════════════════════
// PAGE: PROJECT — pickers + event handlers + save (lot B.10)
// Migration S5.1 du plan TS+Vite (2026-05-10) : .js → .ts strict.
// ═══════════════════════════════════════════════════════════════════════════
//
// _collectAllActions, _actionPickerModal, showCopyActionModal,
// showLinkActionModal, _handleRiskScaleClick, _handleEditLinkedAction,
// _handleAddActionClick, onRiskTableClick/Change/Input, syncActionField,
// saveUpdate.
//
// Bare refs : ps, normalizeIds/Action, reRenderTable, updateStats,
// getDefaultStatusId, showEditActionModal, showAddRiskModal, showRiskHistory,
// trendHtml, sevColor, sevLabel, _removeUnloadGuard, openModal, closeModal,
// router, api.

interface ActionPicker extends Record<string, unknown> {
  id?: number | null;
  text?: string;
  status_id?: number | null;
  approver_id?: number | null;
  approver_name?: string | null;
  priority?: number;
  deadline?: string | null;
  deadline_revised?: string | null;
  performers?: number[] | string;
  consulted?: number[] | string;
  informed?: number[] | string;
  origins?: unknown[];
  project_ids?: number[];
  _riskCategory?: string;
  _riskCode?: string;
  _riskId?: number;
}

function _collectAllActions(): ActionPicker[] {
  const all: ActionPicker[] = [];
  const risks = ps.risks || [];
  const entries = ps.entries || {};
  for (const r of risks) {
    const e = entries[r.id];
    if (!e || !e.actions) continue;
    for (const a of e.actions) {
      if (a['text'] && String(a['text']).trim()) {
        all.push({
          ...(a as ActionPicker),
          _riskCategory: r.category || '—',
          _riskCode: r.code || '',
          _riskId: r.id,
        });
      }
    }
  }
  return all;
}

async function _actionPickerModal(
  title: string,
  confirmLabel: string,
  onConfirm: (a: ActionPicker) => void,
): Promise<void> {
  let actionsAll: ActionPicker[];
  let projects: Array<{ id: number; code?: string | null; name?: string | null }>;
  // Animation d'attente : le fetch peut durer quelques centaines de ms. On
  // ouvre immédiatement la modale avec un spinner (`.loading`) pour donner un
  // retour visuel ; l'openModal final la remplace au succès, closeModal() en
  // cas d'erreur.
  openModal(title, `<div class="loading">${esc(t('common.loading'))}</div>`, () => {}, null);
  try {
    // Picker lier/copier (page révision) : on ne propose que des actions EN
    // COURS (include_completed=false). Le filtre client `eligible` reste en
    // garde-fou (les statuts finaux locaux), mais le serveur ne renvoie déjà
    // plus ni terminées ni fusionnées.
    [actionsAll, projects] = (await Promise.all([
      api.getAllActions({ includeCompleted: false }),
      api.getProjects(),
    ])) as [ActionPicker[], Array<{ id: number; code?: string | null; name?: string | null }>];
  } catch (e: unknown) {
    closeModal();
    const msg = e instanceof Error ? e.message : t('common.error');
    toast(t('pageProject.actionPicker.errLoad', { msg }), 'error');
    return;
  }

  const finalStatusIds = new Set(
    (ps.actionStatuses || []).filter((s) => s.is_final).map((s) => s.id),
  );
  const eligible = actionsAll.filter((a) => !finalStatusIds.has(a.status_id ?? -1));

  if (!eligible.length) {
    closeModal();
    toast(t('pageProject.actionPicker.empty'), 'info');
    return;
  }

  const projectsSorted = [...projects].sort((a, b) =>
    (a.code || a.name || '').localeCompare(b.code || b.name || ''),
  );

  // Le picker est ouvert depuis une révision : on pré-positionne le filtre sur
  // le projet en cours de révision (ps.project) plutôt que sur « tous ». Garde-
  // fou : si ce projet n'est pas dans la liste retournée (droits), on retombe
  // sur 'all'.
  const currentProjectId = ps.project?.id ?? null;
  const hasCurrentProject =
    currentProjectId != null && projectsSorted.some((p) => p.id === currentProjectId);
  let projectFilter: string = hasCurrentProject ? String(currentProjectId) : 'all';

  const projectOptionsHtml = `
    <option value="all"${projectFilter === 'all' ? ' selected' : ''}>${esc(t('pageProject.actionPicker.allProj'))}</option>
    <option value="none"${projectFilter === 'none' ? ' selected' : ''}>${esc(t('pageProject.actionPicker.noProj'))}</option>
    ${projectsSorted
      .map(
        (p) =>
          `<option value="${p.id}"${projectFilter === String(p.id) ? ' selected' : ''}>${esc(p.code ? p.code + ' — ' : '')}${esc(p.name || '')}</option>`,
      )
      .join('')}
  `;

  const usersById = new Map<number, PageUser>((ps.users || []).map((u) => [u.id, u]));
  const statusesById = new Map<number, PageActionStatus>(
    (ps.actionStatuses || []).map((s) => [s.id, s]),
  );
  const PRIO = [
    t('pageProject.actionPicker.prio0'),
    t('pageProject.actionPicker.prio1'),
    t('pageProject.actionPicker.prio2'),
    t('pageProject.actionPicker.prio3'),
    t('pageProject.actionPicker.prio4'),
  ];

  function applyFilters(textFilter: string): ActionPicker[] {
    const lower = (textFilter || '').toLowerCase();
    return eligible.filter((a) => {
      const ids = (a.project_ids as number[] | undefined) || [];
      if (projectFilter === 'all') {
        // pass
      } else if (projectFilter === 'none') {
        if (ids.length > 0) return false;
      } else {
        const pid = parseInt(projectFilter, 10);
        if (!ids.includes(pid)) return false;
      }
      if (lower) {
        if (
          !((a.text as string | undefined) || '').toLowerCase().includes(lower) &&
          !String(a.id || '').includes(lower)
        )
          return false;
      }
      return true;
    });
  }

  function renderRows(textFilter: string): string {
    const filtered = applyFilters(textFilter);
    if (!filtered.length) {
      return `<p style="color:var(--text3);font-size:12px;padding:8px 0">${esc(t('pageProject.actionPicker.noResult'))}</p>`;
    }
    return filtered
      .map((a) => {
        const codeLabel = a.id
          ? `<span style="font-family:var(--font-mono);font-size:11px;font-weight:600;min-width:30px;display:inline-block;color:var(--accent)">#${a.id}</span>`
          : '';
        const approver = a.approver_id ? usersById.get(a.approver_id) : null;
        const approverName = approver ? approver.name : a.approver_name || '—';
        const status = statusesById.get(a.status_id ?? -1);
        const statusName = status ? status.name : '—';
        const prioLabel = PRIO[a.priority ?? -1] || '—';
        const deadline = a.deadline_revised || a.deadline || '—';

        return `<label style="display:flex;align-items:flex-start;gap:8px;padding:7px 0;border-bottom:0.5px solid var(--border);cursor:pointer">
        <input type="radio" name="action-pick" value="${a.id}" style="margin-top:3px;flex-shrink:0">
        <div style="flex:1;min-width:0">
          <div style="display:flex;gap:6px;align-items:center">${codeLabel}<span style="font-size:13px">${esc(a.text || '—')}</span></div>
          <div style="font-size:11px;color:var(--text3);margin-top:2px">
            ${esc(approverName || '—')} · ${esc(prioLabel)} · ${esc(t('pageProject.actionPicker.deadline'))} ${esc(deadline)} · ${esc(statusName || '—')}
          </div>
        </div>
      </label>`;
      })
      .join('');
  }

  const body = `
    <div style="display:flex;flex-direction:column;gap:8px;margin-bottom:10px">
      <select id="action-project-filter" style="width:100%;padding:5px 8px;font-size:13px;border:1px solid var(--border2);border-radius:var(--radius)">
        ${projectOptionsHtml}
      </select>
      <input type="text" id="action-search" placeholder="${esc(t('pageProject.actionPicker.search'))}" style="width:100%;padding:5px 8px;font-size:13px;border:1px solid var(--border2);border-radius:var(--radius)">
    </div>
    <div id="action-list" style="max-height:420px;overflow-y:auto">${renderRows('')}</div>
  `;

  openModal(
    title,
    body,
    () => {
      const picked = document.querySelector<HTMLInputElement>('input[name="action-pick"]:checked');
      if (!picked) {
        toast(t('pageProject.actionPicker.errPick'), 'error');
        return;
      }
      const aid = parseInt(picked.value, 10);
      const a = eligible.find((x) => x.id === aid);
      if (!a) {
        toast(t('pageProject.actionPicker.errMiss'), 'error');
        return;
      }
      onConfirm(a);
    },
    confirmLabel,
  );

  setTimeout(() => {
    const sel = document.getElementById('action-project-filter') as HTMLSelectElement | null;
    const inp = document.getElementById('action-search') as HTMLInputElement | null;
    const list = document.getElementById('action-list');
    function refresh(): void {
      if (list) list.innerHTML = renderRows(inp ? inp.value : '');
    }
    sel?.addEventListener('change', (e: Event) => {
      const target = e.target as HTMLSelectElement;
      projectFilter = target.value;
      refresh();
    });
    inp?.addEventListener('input', refresh);
  }, 0);
}

function showCopyActionModal(destRiskId: number): void {
  void _actionPickerModal(
    t('pageProject.copy.title', { object: t('vocabulary.action') }),
    t('pageProject.copy.btn'),
    (src) => {
      const entries = ps.entries || {};
      const destEntry = entries[destRiskId];
      if (!src || !destEntry) return;
      closeModal();
      commitNewActions(destRiskId);
      const template = {
        text: src.text || '',
        deadline: src.deadline || null,
        deadline_revised: null,
        approver_id: src.approver_id || null,
        performers: [...normalizeIds(src.performers)],
        consulted: [...normalizeIds(src.consulted)],
        informed: [...normalizeIds(src.informed)],
        priority: typeof src.priority === 'number' ? src.priority : 2,
        status_id: src.status_id || getDefaultStatusId(),
        progress: 0,
        origins: [],
        project_ids: ps.project ? [ps.project.id] : [],
      };
      window.showEditActionModal(
        template as never,
        {
          users: ps.users,
          statuses: ps.actionStatuses,
          projects: ps.allProjects || [],
          onSaved: (data: PageAction, pendingNotes: unknown[]) => {
            const newAction: PageAction = {
              _isNew: true,
              _tempId: 'new_' + Date.now(),
              project_risk_id: destRiskId,
              text: data.text || '',
              deadline: data.deadline || null,
              deadline_revised: data.deadline_revised || null,
              status_id: data.status_id || getDefaultStatusId(),
              progress: data.progress || 0,
              approver_id: data.approver_id || null,
              priority: data.priority ?? 2,
              performers: data.performers || [],
              consulted: data.consulted || [],
              informed: data.informed || [],
              _pendingNotes: pendingNotes || [],
            };
            destEntry.actions.push(newAction);
            ps.newActions = ps.newActions || [];
            ps.newActions.push(newAction);
            reRenderTable();
          },
        } as never,
      );
    },
  );
}

function showLinkActionModal(destRiskId: number): void {
  void _actionPickerModal(
    t('pageProject.link.title', { object: t('vocabulary.action') }),
    t('pageProject.link.btn'),
    (src) => {
      const entries = ps.entries || {};
      const entry = entries[destRiskId];
      if (!src || !entry) return;
      const existingIds = entry.linked_action_ids || [];
      if (src.id && existingIds.includes(src.id)) {
        toast(t('pageProject.link.alreadyLinked'), 'error');
        return;
      }
      if (src.id == null) return;
      entry.linked_action_ids = [...existingIds, src.id];
      const linkEntry: PageAction = { ...(src as PageAction), linked: true };
      if (src._riskId !== undefined) linkEntry.source_project_risk_id = src._riskId;
      entry.linked_actions = [...(entry.linked_actions || []), linkEntry];
      ps.newLinks = ps.newLinks || [];
      ps.newLinks.push({ project_risk_id: destRiskId, source_action_id: src.id });
      closeModal();
      reRenderTable();
    },
  );
}

function _handleRiskScaleClick(riskId: number, type: string, val: number): void {
  const entries = ps.entries || {};
  const entry = entries[riskId];
  if (!entry) return;
  if (type === 'prob') entry.probability = val;
  else entry.impact = val;
  ps.dirtyRisks?.add(riskId);
  const cell = document.getElementById(
    type === 'prob' ? `prob-cell-${riskId}` : `impact-cell-${riskId}`,
  );
  if (cell) {
    cell.querySelectorAll<HTMLElement>('.scale-btn').forEach((btn) => {
      const n = parseInt(btn.dataset['scaleVal'] || '0');
      const isImpact = type === 'impact';
      btn.className =
        'scale-btn' +
        (n === val ? ` active-${n}` : '') +
        (n === 5 ? ' problem' : '') +
        (isImpact ? ' impact-btn' : '');
    });
  }
  const sev = (entry.probability || 1) * (entry.impact || 1);
  const col = sevColor(sev);
  const sevEl = document.getElementById(`sev-${riskId}`);
  if (sevEl) {
    sevEl.className = 'sev';
    sevEl.style.color = col;
    const sevValue = sevEl.querySelector('.sev-value');
    if (sevValue) sevValue.textContent = String(sev);
    const sevLbl = sevEl.querySelector('.sev-label');
    if (sevLbl) sevLbl.textContent = sevLabel(sev);
  }
  const trendCell = document.getElementById(`trend-${riskId}`);
  if (trendCell) trendCell.innerHTML = trendHtml(riskId, sev, entry.is_relevant !== false);
  updateStats();
}

function _handleEditLinkedAction(actionId: number, _riskId: number): void {
  let sourceAction: PageAction | null = null;
  const risks = ps.risks || [];
  const entries = ps.entries || {};
  for (const r of risks) {
    const e2 = entries[r.id];
    if (!e2) continue;
    const found = (e2.actions || []).find((a) => a.id === actionId);
    if (found) {
      sourceAction = found;
      break;
    }
    const foundLinked = (e2.linked_actions || []).find((a) => a.id === actionId);
    if (foundLinked) {
      sourceAction = foundLinked;
      break;
    }
  }
  if (!sourceAction) {
    toast(t('pageProject.linkedAction.errSrc'), 'error');
    return;
  }
  window.showEditActionModal(
    sourceAction as never,
    {
      users: ps.users,
      statuses: ps.actionStatuses,
      projects: ps.allProjects || [],
      onSaved: async (updated: PageAction) => {
        try {
          if (updated.id == null) return;
          await api.updateAction(updated.id, {
            text: updated.text,
            deadline: updated.deadline,
            deadline_revised: updated.deadline_revised,
            status_id: updated.status_id,
            progress: updated.progress,
            priority: updated.priority,
            approver_id: updated.approver_id,
            performers: updated.performers,
            consulted: updated.consulted,
            informed: updated.informed,
            committee_ids: updated.committee_ids || [],
          });
          if (ps.revision) {
            const revData = (await api.getRevision(ps.revision.id)) as {
              entries?: Record<string, { linked_actions?: PageAction[] }>;
            };
            for (const r of risks) {
              const fresh = revData.entries && revData.entries[String(r.id)];
              const target = entries[r.id];
              if (fresh && target) {
                target.linked_actions = fresh.linked_actions || [];
                target.linked_action_ids = (fresh.linked_actions || []).map((a) => a.id ?? 0);
              }
            }
          }
          reRenderTable();
          toast(t('pageProject.linkedAction.toast'));
        } catch (err: unknown) {
          const msg = err instanceof Error ? err.message : t('common.error');
          toast(msg, 'error');
        }
      },
    } as never,
  );
}

function _handleAddActionClick(riskId: number): void {
  const entries = ps.entries || {};
  const entry = entries[riskId];
  if (!entry) return;
  const template: PageAction = {
    text: '',
    priority: 2,
    approver_id: null,
    deadline: todayStr(),
    deadline_revised: null,
    status_id: getDefaultStatusId(),
    progress: 0,
    performers: [],
    consulted: [],
    informed: [],
    origins: [],
    origin: '',
    project_ids: ps.project ? [ps.project.id] : [],
  };
  window.showEditActionModal(
    template as never,
    {
      users: ps.users,
      statuses: ps.actionStatuses,
      projects: ps.allProjects || [],
      onSaved: (data: PageAction, pendingNotes: unknown[]) => {
        const newAction: PageAction = {
          _isNew: true,
          _tempId: 'new_' + Date.now(),
          project_risk_id: riskId,
          text: data.text || '',
          deadline: data.deadline || null,
          deadline_revised: data.deadline_revised || null,
          status_id: data.status_id || getDefaultStatusId(),
          progress: data.progress || 0,
          approver_id: data.approver_id || null,
          priority: data.priority ?? 2,
          performers: data.performers || [],
          consulted: data.consulted || [],
          informed: data.informed || [],
          project_ids: data.project_ids || (ps.project ? [ps.project.id] : []),
          origins: data.origins || [],
          origin: data.origin || '',
          _pendingNotes: pendingNotes || [],
        };
        entry.actions.push(newAction);
        ps.newActions = ps.newActions || [];
        ps.newActions.push(newAction);
        reRenderTable();
      },
    } as never,
  );
}

function onRiskTableClick(e: Event): void {
  const target = e.target as HTMLElement | null;
  if (!target) return;
  const entries = ps.entries || {};
  const risks = ps.risks || [];

  const gotoRiskBtn = target.closest<HTMLElement>('[data-goto-risk]');
  if (gotoRiskBtn) {
    const targetRiskId = parseInt(gotoRiskBtn.dataset['gotoRisk'] || '0');
    if (targetRiskId) {
      ps.expandedRiskId = targetRiskId;
      reRenderTable();
      setTimeout(() => {
        const row = document.querySelector(`tr[data-risk-id="${targetRiskId}"]`);
        if (row) row.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 50);
    }
    return;
  }

  const unlinkBtn = target.closest<HTMLElement>('[data-unlink-action]');
  if (unlinkBtn) {
    const actionId = parseInt(unlinkBtn.dataset['unlinkAction'] || '0');
    const riskId = parseInt(unlinkBtn.dataset['unlinkRisk'] || '0');
    const entry = entries[riskId];
    if (entry) {
      const lnk = (entry.linked_actions || []).find((a) => a.id === actionId);
      if (lnk && lnk.link_id) {
        ps.deletedLinkIds = ps.deletedLinkIds || [];
        ps.deletedLinkIds.push(lnk.link_id);
      }
      entry.linked_action_ids = (entry.linked_action_ids || []).filter((id) => id !== actionId);
      entry.linked_actions = (entry.linked_actions || []).filter((a) => a.id !== actionId);
      ps.newLinks = (ps.newLinks || []).filter(
        (l) => !(l.project_risk_id === riskId && l.source_action_id === actionId),
      );
      reRenderTable();
    }
    return;
  }

  const tagAddBtn = target.closest<HTMLElement>('.tag-add-btn');
  if (tagAddBtn) {
    openUserPickerModal(
      tagAddBtn.dataset['tagField'] || '',
      parseInt(tagAddBtn.dataset['tagRisk'] || '0'),
      parseInt(tagAddBtn.dataset['tagIdx'] || '0'),
    );
    return;
  }

  const removeTag = target.closest<HTMLElement>('[data-remove-tag]');
  if (removeTag) {
    const field = removeTag.dataset['field'] || '';
    const riskId = parseInt(removeTag.dataset['actionRisk'] || '0');
    const idx = parseInt(removeTag.dataset['actionIdx'] || '0');
    const uid = parseInt(removeTag.dataset['removeTag'] || '0');
    const entry = entries[riskId];
    const action = entry?.actions?.[idx];
    if (entry && action) {
      const current = (action as Record<string, unknown>)[field];
      const filtered = normalizeIds(current).filter((id) => id !== uid);
      (action as Record<string, unknown>)[field] = filtered;
      const wrap = removeTag.closest<HTMLElement>('.tag-select-wrap');
      if (wrap) refreshTagWrap(wrap, field, riskId, idx, filtered);
    }
    return;
  }

  const histBtn = target.closest<HTMLElement>('[data-history-risk]');
  if (histBtn) {
    void showRiskHistory(parseInt(histBtn.dataset['historyRisk'] || '0'));
    return;
  }

  const th = target.closest<HTMLElement>('th[data-sort]');
  if (th) {
    const key = th.dataset['sort'] || '';
    if (ps.sortBy === key) ps.sortDir = ps.sortDir === 'asc' ? 'desc' : 'asc';
    else {
      ps.sortBy = key;
      ps.sortDir = 'desc';
    }
    applySort();
    reRenderTable();
    return;
  }

  const scaleBtn = target.closest<HTMLElement>('[data-scale-risk]');
  if (scaleBtn) {
    _handleRiskScaleClick(
      parseInt(scaleBtn.dataset['scaleRisk'] || '0'),
      scaleBtn.dataset['scaleType'] || '',
      parseInt(scaleBtn.dataset['scaleVal'] || '0'),
    );
    return;
  }

  const linkActionBtn = target.closest<HTMLElement>('[data-link-action-risk]');
  if (linkActionBtn) {
    showLinkActionModal(parseInt(linkActionBtn.dataset['linkActionRisk'] || '0'));
    return;
  }

  const editLinkedBtn = target.closest<HTMLElement>('[data-edit-linked-action]');
  if (editLinkedBtn) {
    _handleEditLinkedAction(
      parseInt(editLinkedBtn.dataset['editLinkedAction'] || '0'),
      parseInt(editLinkedBtn.dataset['editLinkedRisk'] || '0'),
    );
    return;
  }

  const editInlineBtn = target.closest<HTMLElement>('[data-edit-action-inline]');
  if (editInlineBtn) {
    const riskId = parseInt(editInlineBtn.dataset['editActionRisk'] || '0');
    const idx = parseInt(editInlineBtn.dataset['editActionInline'] || '0');
    const entry = entries[riskId];
    const a = entry?.actions?.[idx];
    if (!a) return;
    window.showEditActionModal(
      a as never,
      {
        users: ps.users,
        statuses: ps.actionStatuses,
        projects: ps.allProjects || [],
        onSaved: (updated: PageAction) => {
          Object.assign(a, normalizeAction(updated));
          reRenderTable();
        },
      } as never,
    );
    return;
  }

  const editDeadlineBtn = target.closest<HTMLElement>('[data-edit-deadline-risk]');
  if (editDeadlineBtn) {
    const riskId = parseInt(editDeadlineBtn.dataset['editDeadlineRisk'] || '0');
    const idx = parseInt(editDeadlineBtn.dataset['editDeadlineIdx'] || '0');
    const entry = entries[riskId];
    const a = entry?.actions?.[idx];
    if (!a) return;
    openModal(
      t('pageProject.editDeadline.title'),
      `<p style="color:var(--warn);font-size:13px;margin-bottom:12px">${esc(t('pageProject.editDeadline.warn'))}</p>
       <div class="form-group"><label>${esc(t('pageProject.editDeadline.label'))}</label>
         <input type="date" id="edit-deadline-val" value="${esc(a.deadline || '')}"></div>`,
      () => {
        const dateEl = document.getElementById('edit-deadline-val') as HTMLInputElement | null;
        const val = dateEl?.value || null;
        const targetAction = entry.actions[idx];
        if (targetAction) targetAction.deadline = val;
        closeModal();
        reRenderTable();
      },
      t('pageProject.editDeadline.btn'),
    );
    return;
  }

  const copyActionBtn = target.closest<HTMLElement>('[data-copy-action-risk]');
  if (copyActionBtn) {
    showCopyActionModal(parseInt(copyActionBtn.dataset['copyActionRisk'] || '0'));
    return;
  }

  const expandBtn = target.closest<HTMLElement>('[data-expand-risk]');
  if (expandBtn) {
    const riskId = parseInt(expandBtn.dataset['expandRisk'] || '0');
    if (ps.expandedRiskId && ps.expandedRiskId !== riskId) commitNewActions(ps.expandedRiskId);
    ps.expandedRiskId = ps.expandedRiskId === riskId ? null : riskId;
    reRenderTable();
    return;
  }

  const addActionBtn = target.closest<HTMLElement>('[data-add-action-risk]');
  if (addActionBtn) {
    _handleAddActionClick(parseInt(addActionBtn.dataset['addActionRisk'] || '0'));
    return;
  }

  const delActionBtn = target.closest<HTMLElement>('[data-del-action-risk]');
  if (delActionBtn) {
    const riskId = parseInt(delActionBtn.dataset['delActionRisk'] || '0');
    const idx = parseInt(delActionBtn.dataset['delActionIdx'] || '0');
    const entry = entries[riskId];
    const a = entry?.actions?.[idx];
    const label =
      a && a.text
        ? `"${a.text.slice(0, 40)}${a.text.length > 40 ? '…' : ''}"`
        : t('pageProject.delAction.fallback');
    openModal(
      t('pageProject.delAction.title', { object: t('vocabulary.action') }),
      t('pageProject.delAction.body', { label }),
      () => {
        const ent = entries[riskId];
        if (ent) {
          const action = ent.actions[idx];
          if (action && action.id && !action._isNew) {
            ps.deletedActionIds = ps.deletedActionIds || [];
            ps.deletedActionIds.push(action.id);
          }
          if (action && action._isNew && action._tempId) {
            ps.newActions = (ps.newActions || []).filter((x) => x._tempId !== action._tempId);
          }
          ent.actions.splice(idx, 1);
        }
        closeModal();
        reRenderTable();
      },
      t('pageProject.delAction.btn'),
    );
    return;
  }

  const editRiskBtn = target.closest<HTMLElement>('[data-edit-risk]');
  if (editRiskBtn) {
    const riskId = parseInt(editRiskBtn.dataset['editRisk'] || '0');
    const risk = risks.find((r) => r.id === riskId);
    if (risk) void showAddRiskModal(risk);
    return;
  }

  const delRiskBtn = target.closest<HTMLElement>('[data-del-risk]');
  if (delRiskBtn) {
    const riskId = parseInt(delRiskBtn.dataset['delRisk'] || '0');
    const risk = risks.find((r) => r.id === riskId);
    const label = risk
      ? `${risk.code ? risk.code + ' — ' : ''}${risk.category || t('pageProject.delRisk.fallback')}`
      : t('pageProject.delRisk.fallback');
    openModal(
      t('pageProject.delRisk.title'),
      t('pageProject.delRisk.body', { label }),
      async () => {
        try {
          if (!ps.project) return;
          await api.deleteProjectRisk(ps.project.id, riskId);
          ps.risks = (ps.risks || []).filter((r) => r.id !== riskId);
          ps.stableOrder = (ps.stableOrder || []).filter((id) => id !== riskId);
          if (ps.entries) delete ps.entries[riskId];
          closeModal();
          toast(t('pageProject.delRisk.toast'));
          reRenderTable();
          updateStats();
        } catch (err: unknown) {
          const msg = err instanceof Error ? err.message : t('common.error');
          toast(msg, 'error');
        }
      },
      t('pageProject.delRisk.btn'),
    );
  }
}

function onRiskTableChange(e: Event): void {
  const target = e.target as HTMLElement | null;
  if (!target) return;
  const entries = ps.entries || {};

  if (target.classList.contains('relevant-chk')) {
    const chk = target as HTMLInputElement;
    const riskId = parseInt(chk.dataset['relevantRisk'] || '0');
    const entry = entries[riskId];
    if (entry) {
      entry.is_relevant = chk.checked;
      ps.dirtyRisks?.add(riskId);
      reRenderTable();
      updateStats();
    }
    return;
  }
  if (target.classList.contains('escalate-chk')) {
    const chk = target as HTMLInputElement;
    const riskId = parseInt(chk.dataset['escalateRisk'] || '0');
    void _toggleEscalateRisk(riskId, chk.checked, chk);
    return;
  }
  if (target.classList.contains('entry-owner')) {
    const sel = target as HTMLSelectElement;
    const riskId = parseInt(sel.dataset['ownerRisk'] || '0');
    const entry = entries[riskId];
    if (entry) {
      entry.owner_id = parseInt(sel.value) || null;
      ps.dirtyRisks?.add(riskId);
    }
    return;
  }
  const ar = target.dataset['actionRisk'];
  const ai = target.dataset['actionIdx'];
  if (ar !== undefined && ai !== undefined) {
    syncActionField(target, parseInt(ar), parseInt(ai));
  }
}

async function _toggleEscalateRisk(
  riskId: number,
  checked: boolean,
  chk: HTMLInputElement,
): Promise<void> {
  const risks = ps.risks || [];
  const r = risks.find((rr) => rr.id === riskId);
  if (!ps.project || !r) return;
  // PUT immédiat. Le endpoint remplace TOUS les champs (ProjectRiskIn), on
  // renvoie donc le payload complet du risque + le flag basculé (même forme
  // que la modale d'édition).
  const data = {
    code: r.code || null,
    cause: r.cause || '',
    consequence: r.consequence || '',
    category: r.category || '',
    visibility: r.visibility || 'publique',
    stages: r.stages || [],
    escalated_to_program: checked,
  };
  try {
    const updated = (await api.updateProjectRisk(ps.project.id, riskId, data)) as PageRisk;
    const idx = risks.findIndex((rr) => rr.id === riskId);
    if (idx !== -1) risks[idx] = updated;
    toast(checked ? t('pageProject.escalate.toastOn') : t('pageProject.escalate.toastOff'));
    reRenderTable();
    updateStats();
  } catch (err: unknown) {
    chk.checked = !checked; // rollback visuel
    const msg = err instanceof Error ? err.message : t('common.error');
    toast(msg, 'error');
  }
}

function onRiskTableInput(e: Event): void {
  const target = e.target as HTMLElement | null;
  if (!target) return;
  const ar = target.dataset['actionRisk'];
  const ai = target.dataset['actionIdx'];
  if (ar !== undefined && ai !== undefined) {
    syncActionField(target, parseInt(ar), parseInt(ai));
  }
}

function syncActionField(el: HTMLElement, riskId: number, idx: number): void {
  const entries = ps.entries || {};
  const entry = entries[riskId];
  const action = entry?.actions?.[idx];
  if (!entry || !action) return;
  const field = el.dataset['field'];
  if (!field) return;
  const valueEl = el as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
  const value = valueEl.value;
  if (field === 'approver_id') {
    action.approver_id = parseInt(value) || null;
  } else if (field === 'priority') {
    action.priority = parseInt(value);
  } else if (field === 'status_id') {
    action.status_id = parseInt(value) || null;
    const row = el.closest<HTMLElement>('tr.action-row');
    if (row) {
      const ta = row.querySelector<HTMLTextAreaElement>('textarea[data-field="text"]');
      if (ta) {
        const st = (ps.actionStatuses || []).find((s) => s.id === action.status_id);
        ta.style.color = st ? st.color || '' : '';
        ta.style.fontWeight = st && st.bold ? '700' : '';
        ta.style.fontStyle = st && st.italic ? 'italic' : '';
        ta.style.textDecoration = st && st.strikethrough ? 'line-through' : '';
      }
    }
  } else if (field === 'progress') {
    const v = Math.max(0, Math.min(100, parseInt(value) || 0));
    action.progress = v;
    const row = el.closest<HTMLElement>('tr.action-row');
    if (row) {
      const bar = row.querySelector<HTMLElement>('div > div[style*="height:100%"]');
      if (bar) {
        const color = v >= 100 ? 'var(--ok)' : v >= 50 ? 'var(--accent)' : 'var(--warn)';
        bar.style.width = v + '%';
        bar.style.background = color;
      }
    }
  } else {
    (action as Record<string, unknown>)[field] = value;
  }
}

async function saveUpdate(): Promise<void> {
  if (!ps.revision || ps.revision.is_closed) return;
  const btn = document.getElementById('btn-save-update') as HTMLButtonElement | null;
  if (btn) btn.disabled = true;
  try {
    const risks = ps.risks || [];
    for (const r of risks) commitNewActions(r.id);

    const entries = ps.entries || {};
    const entriesPayload: Array<{
      project_risk_id: number;
      probability: number;
      impact: number;
      is_relevant: boolean;
      owner_id: number | null;
    }> = [];
    if (ps.dirtyRisks) {
      for (const riskId of ps.dirtyRisks) {
        const e = entries[riskId];
        const isRel = e?.is_relevant !== false;
        entriesPayload.push({
          project_risk_id: riskId,
          probability: isRel ? (e?.probability ?? 1) : 1,
          impact: isRel ? (e?.impact ?? 1) : 1,
          is_relevant: isRel,
          owner_id: e?.owner_id || null,
        });
      }
    }

    const new_actions = (ps.newActions || []).map((a) => ({
      project_risk_id: a.project_risk_id,
      code: null,
      text: a.text || '',
      deadline: a.deadline || null,
      deadline_revised: a.deadline_revised || null,
      status_id: a.status_id || null,
      progress: typeof a.progress === 'number' ? a.progress : 0,
      approver_id: a.approver_id || null,
      performers: normalizeIds(a.performers),
      consulted: normalizeIds(a.consulted),
      informed: normalizeIds(a.informed),
      priority: typeof a.priority === 'number' ? a.priority : 2,
      project_ids: normalizeIds(a.project_ids),
      pending_notes: a._pendingNotes || [],
    }));

    const result = (await api.saveRevision(ps.revision.id, {
      entries: entriesPayload,
      new_actions,
      deleted_action_ids: ps.deletedActionIds,
      new_links: ps.newLinks,
      deleted_link_ids: ps.deletedLinkIds,
    })) as { entries: Record<string, { actions: PageAction[]; linked_actions?: PageAction[] }> };

    ps.dirtyRisks = new Set();
    ps.newActions = [];
    ps.deletedActionIds = [];
    ps.newLinks = [];
    ps.deletedLinkIds = [];

    const fresh = result.entries;
    for (const r of risks) {
      const fe = fresh[String(r.id)];
      const target = entries[r.id];
      if (fe && target) {
        target.actions = fe.actions.map((a) => normalizeAction(a));
        target.linked_actions = fe.linked_actions || [];
        target.linked_action_ids = (fe.linked_actions || []).map((a) => a.id ?? 0);
      }
    }
    reRenderTable();
    toast(t('pageProject.save.toast'));
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : t('common.error');
    toast(t('pageProject.save.error', { msg }), 'error');
  } finally {
    if (btn) btn.disabled = false;
  }
}

// ── ESM globals shim ─────────────────────────────────────────────────────
Object.assign(window, {
  _collectAllActions,
  _actionPickerModal,
  showCopyActionModal,
  showLinkActionModal,
  _handleRiskScaleClick,
  _handleEditLinkedAction,
  _handleAddActionClick,
  onRiskTableClick,
  onRiskTableChange,
  onRiskTableInput,
  syncActionField,
  saveUpdate,
});

export {};
